import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { BehaviorSubject, catchError, filter, finalize, firstValueFrom } from 'rxjs'
import { HttpErrorHandlerService } from '../helpers/http-error-handler.service'
import { IUpdateDTModel } from '../helpers/update-model.interface'
import {
  IAppDashboard,
  IAppDashboardTypeFlags,
  IAppDashboardTypes,
  ICreateAppDashboard,
} from './app-dashboard.interface'
import { Observable } from 'rxjs/internal/Observable'
import { IAppPluginWidget } from '../app-plugin-widget/app-plugin-widget.interface'
import { API_ENDPOINT_PATH, API_HTTP_HEADERS } from "../helpers/api-constant";


@Injectable({
  providedIn: 'root'
})
export class AppDashboardService {
  readonly #headers: HttpHeaders
  readonly #urlEndpoint: string
  private readonly appDashboardTypeFlags: { copy: IAppDashboardTypeFlags, view: IAppDashboardTypeFlags } = {
    copy: {
      edit: true,
      delete: true
    },
    view: {
      edit: false,
      delete: false
    }
  }

  private readonly appDashboardSubject$: BehaviorSubject<IAppDashboard> = new BehaviorSubject<IAppDashboard>({
    _id: '-1',
    enabled: false,
    type: 'view',
    roleAssignable: true,
    name: 'Test-Menu',
    menu: [],
  })
  private readonly appDashboardsSubject$: BehaviorSubject<IAppDashboard[]> = new BehaviorSubject<IAppDashboard[]>([]);

  private readonly appDashboard$: Observable<IAppDashboard> = this.appDashboardSubject$.asObservable()
    .pipe(
      filter((dashboard: IAppDashboard): dashboard is IAppDashboard => dashboard._id !== '-1'),
      finalize(() => console.log('appDashboard$ - Observable completed.'))
    )

  constructor(
    private readonly http: HttpClient,
    private readonly errorHandlerService: HttpErrorHandlerService
  ) {
	  this.#urlEndpoint = `${API_ENDPOINT_PATH}/appDashboard`;
	  this.#headers = new HttpHeaders(API_HTTP_HEADERS);
  }

  private getUrl(): string {
    return `${this.#urlEndpoint}`
  }

  private getHeader(): HttpHeaders {
    return this.#headers
  }

  getActiveAppDashboard(): IAppDashboard {
    return this.appDashboardSubject$.getValue()
  }

  setActiveAppDashboard(dashboard: IAppDashboard): void {
    this.appDashboardSubject$.next(dashboard)
  }

  observeActiveAppDashboard$(): Observable<IAppDashboard> {
    return this.appDashboard$
  }


  getAppDashboards(): IAppDashboard[] {
    return this.appDashboardsSubject$.getValue()
  }

  setAppDashboards(dashboards: IAppDashboard[]) {
    this.appDashboardsSubject$.next(dashboards)
  }

  getPermissionFlags(type: IAppDashboardTypes): IAppDashboardTypeFlags {
    return this.appDashboardTypeFlags[type]
  }

  getAppDashboardTypes(): string[] {
    return Object.keys(this.appDashboardTypeFlags)
  }

  async upsertOne(payload: IAppDashboard|ICreateAppDashboard): Promise<{}|IAppDashboard|IUpdateDTModel> {
    const result = this.http.post<IAppDashboard>(`${this.getUrl()}`, payload,
      { headers: this.getHeader() }).pipe(catchError(this.errorHandlerService.handleErrorForSingleRecord))
    return await firstValueFrom(result)
  }

  async getById(id: string, attr?: string): Promise<{}|IAppDashboard> {
    let queryUrl = `${this.getUrl()}/${id}`
    if (attr) { queryUrl += `?attr=${attr}`}

    const result = this.http.get<IAppDashboard>(`${queryUrl}`,
      { headers: this.getHeader() }).pipe(catchError(this.errorHandlerService.handleErrorForSingleRecord))
    return await firstValueFrom(result)
  }

  async listAll(params?: string): Promise<IAppDashboard[]> {
    let queryUrl = `${this.getUrl()}`
    if (params) { queryUrl += `?${params}` }

    const result = this.http.get<IAppDashboard[]>(`${queryUrl}`,
      { headers: this.getHeader() }).pipe(catchError(this.errorHandlerService.handleErrorForRecords))
    return await firstValueFrom(result)
  }

  async deleteById(id: string): Promise<void> {
	  const headers = this.getHeader().delete('Content-Type').delete('content-type');
    const result = this.http.delete(`${this.getUrl()}/${id}`,
      { headers: headers }).pipe(catchError(this.errorHandlerService.handleError))
    await firstValueFrom(result)
  }

  async listAppPluginWidgets(id: string): Promise<IAppPluginWidget[]> {
    let queryUrl = `${this.getUrl()}/${id}/appPluginWidget`
	  const headers = this.getHeader().delete('Content-Type').delete('content-type');

    const result = this.http.get<IAppPluginWidget[]>(`${queryUrl}`,
      { headers: headers }).pipe(catchError(this.errorHandlerService.handleErrorForRecords))
    return await firstValueFrom(result)
  }
}
